import * as React from "react";
import { Box } from "@mui/material";
import { useGetFieldAdminTeamInstallersQuery } from "../fieldAdminApi";
import { initialsOf } from "../designTokens";

// IMS is the system-of-record for the installer roster and /field-admin/team/installers/
// reaches it, so the H&S steps read real names instead of asking the FA to type numeric
// ids every morning. RTK Query caches this by endpoint, so moving between H&S steps
// reuses one response rather than re-hitting IMS per page.
export function useTeamRoster() {
  const { data, isSuccess, isError, refetch } = useGetFieldAdminTeamInstallersQuery();

  return React.useMemo(() => {
    const installers = isSuccess ? data.installers : [];
    return {
      installers,
      nameById: new Map(installers.map((installer) => [installer.id, installer.name])),
      isRosterReady: isSuccess,
      // Every H&S step blocks its whole render on isRosterReady, so a failing roster call
      // used to leave them on a shimmer forever — they only rendered their *own* query's
      // error. Exposing these lets each step report and retry the call it's waiting on.
      isRosterError: isError,
      refetchRoster: refetch,
    };
  }, [data, isSuccess, isError, refetch]);
}

// .dev-avatar / .att-avatar and their .c2-.c5 variants — the same five gradients across every
// Field Admin design, at 36px. A flat translucent grey read as a placeholder rather than a
// person, and made every row in a list look alike.
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #6366F1, #8B5CF6)",
  "linear-gradient(135deg, #06B6D4, #0EA5E9)",
  "linear-gradient(135deg, #EC4899, #F43F5E)",
  "linear-gradient(135deg, #10B981, #14B8A6)",
  "linear-gradient(135deg, #F59E0B, #EF4444)",
];

export function InstallerAvatar({ name, id, size = 36 }) {
  const initials = initialsOf(name);
  // Keyed on the installer id, not on list position, so a technician keeps the same colour
  // across every page and between renders as rows are added or filtered.
  const gradient = AVATAR_GRADIENTS[Math.abs(Number(id) || 0) % AVATAR_GRADIENTS.length];
  return (
    <Box
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        background: gradient,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: initials ? "0.82rem" : "0.7rem",
        fontWeight: 700,
        letterSpacing: "-0.01em",
        flexShrink: 0,
      }}
    >
      {initials || `#${id}`}
    </Box>
  );
}

// Merges the team roster with whatever records already exist, keyed by installer id.
// Roster order first (so the list is the team, in a stable order), then any record whose
// installer the roster doesn't know about — those would otherwise silently disappear.
export function mergeRosterRows(installers, records, toRow) {
  const recordsById = new Map(records.map((record) => [record.installer_id, record]));
  const rosterIds = new Set(installers.map((installer) => installer.id));

  return [
    ...installers.map((installer) => toRow(recordsById.get(installer.id), installer.id)),
    ...records.filter((record) => !rosterIds.has(record.installer_id)).map((record) => toRow(record, record.installer_id)),
  ];
}
